import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { ghostButtonClass, primaryButtonClass } from "@/components/base/fieldStyles";
import { formatCOP, formatNumber } from "@/utils/format";
import type { Product } from "@/types";
import type { PdfCatalogRow } from "@/utils/pdfCatalog";

interface Change {
  label: string;
  from: string;
  to: string;
}

interface PreviewItem {
  row: PdfCatalogRow;
  changes: Change[];
}

export interface CatalogImportOptions {
  applyNewPrices: boolean;
  // SKU concretos a los que SÍ se les aplica el precio nuevo. Si se envía,
  // manda sobre applyNewPrices (permite aplicar a unos y a otros no).
  applyPriceSkus?: string[];
}

interface CatalogImportPreviewModalProps {
  open: boolean;
  rows: PdfCatalogRow[];
  products: Product[];
  onClose: () => void;
  onConfirm?: (options: CatalogImportOptions) => void;
  confirming?: boolean;
}

const isPriceLabel = (label: string) => label === "Precio" || label === "Costo base";

// Detalle previo: muestra EXACTAMENTE qué va a pasar al confirmar, usando la
// misma comparación que hace el merge por SKU.
//  · Nuevos      -> se crean como producto nuevo
//  · Actualizar  -> se actualizan solo los campos que cambiaron
//  · Sin cambios -> se omiten (no se tocan)
// Los cambios de PRECIO del PDF se pueden aplicar todos, ninguno, o
// producto por producto con una casilla individual.
const CatalogImportPreviewModal = ({
  open,
  rows,
  products,
  onClose,
  onConfirm,
  confirming = false,
}: CatalogImportPreviewModalProps) => {
  // Ids de producto (fila del PDF) para los que se CONSERVA el precio actual.
  // Todo lo que no esté aquí se actualiza con el precio del PDF.
  const [keepIds, setKeepIds] = useState<string[]>([]);

  // Al abrir, arrancamos aplicando el precio nuevo a todos (comportamiento de siempre).
  useEffect(() => {
    if (open) setKeepIds([]);
  }, [open]);

  const { nuevos, actualizados, omitidos } = useMemo(() => {
    const nuevos: PdfCatalogRow[] = [];
    const omitidos: PdfCatalogRow[] = [];
    const actualizados: PreviewItem[] = [];

    rows.forEach((row) => {
      const sku = row.sku.trim().toLowerCase();
      if (!sku) return;
      const match = products.find((p) => p.sku.trim().toLowerCase() === sku);
      if (!match) {
        nuevos.push(row);
        return;
      }

      const changes: Change[] = [];
      if (row.pricePublic > 0 && Math.round(row.pricePublic) !== Math.round(match.pricePublic)) {
        changes.push({
          label: "Precio",
          from: formatCOP(match.pricePublic),
          to: formatCOP(row.pricePublic),
        });
      }
      // El costo base se recalcula SIEMPRE al 75% del precio de catálogo.
      const targetCostBase =
        row.pricePublic > 0 ? Math.round(row.pricePublic * 0.75) : match.costBase;
      if (targetCostBase !== match.costBase) {
        changes.push({
          label: "Costo base",
          from: formatCOP(match.costBase),
          to: formatCOP(targetCostBase),
        });
      }
      if (row.pv > 0 && row.pv !== match.pv) {
        changes.push({ label: "PV", from: String(match.pv), to: String(row.pv) });
      }
      if (row.name && row.name !== match.name) {
        changes.push({ label: "Nombre", from: match.name, to: row.name });
      }
      if (row.category && row.category !== match.category) {
        changes.push({ label: "Categoría", from: match.category, to: row.category });
      }
      if (row.imageUrl && row.imageUrl !== (match.imageUrl ?? "")) {
        changes.push({ label: "Foto", from: "—", to: "Nueva foto" });
      }

      if (changes.length === 0) omitidos.push(row);
      else actualizados.push({ row, changes });
    });

    return { nuevos, actualizados, omitidos };
  }, [rows, products]);

  // Productos que traen algún cambio de precio/costo (los únicos que necesitan
  // la casilla individual; el resto no cambia de precio).
  const priceItems = useMemo(
    () => actualizados.filter((it) => it.changes.some((c) => isPriceLabel(c.label))),
    [actualizados]
  );

  const isApplied = (item: PreviewItem) => !keepIds.includes(item.row.id);
  const hasPrice = (item: PreviewItem) => item.changes.some((c) => isPriceLabel(c.label));

  // Cambios reales que quedarán tras aplicar la decisión individual.
  const realChanges = (item: PreviewItem) =>
    item.changes.filter((c) => (isPriceLabel(c.label) ? isApplied(item) : true));

  const updatingItems = useMemo(
    () => actualizados.filter((it) => realChanges(it).length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actualizados, keepIds]
  );

  const keptOnly = actualizados.length - updatingItems.length;
  const appliedPriceCount = priceItems.filter(isApplied).length;
  const allApplied = priceItems.length === 0 || appliedPriceCount === priceItems.length;

  const appliedPriceSkus = priceItems.filter(isApplied).map((it) => it.row.sku);

  const handleConfirm = () => {
    onConfirm?.({ applyNewPrices: allApplied, applyPriceSkus: appliedPriceSkus });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revisar y confirmar importación"
      icon="ri-list-check-2"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          {onConfirm && (
            <button onClick={handleConfirm} disabled={confirming} className={primaryButtonClass}>
              <i className={confirming ? "ri-loader-4-line animate-spin" : "ri-download-cloud-2-line"} />
              Confirmar e importar
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-foreground-600">
          Esto es lo que pasará al confirmar. La comparación se hace <strong>por SKU</strong> y solo
          se actualizan los campos que cambiaron.
        </p>

        {/* Elección de precios: aplicar los del PDF o conservar los actuales */}
        {priceItems.length > 0 && (
          <label
            className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
              allApplied
                ? "border-accent-500/40 bg-accent-500/10"
                : "border-secondary-500/40 bg-secondary-500/10"
            }`}
          >
            <input
              type="checkbox"
              checked={allApplied}
              onChange={(e) => setKeepIds(e.target.checked ? [] : priceItems.map((it) => it.row.id))}
              className="mt-0.5 w-4 h-4 accent-accent-500 cursor-pointer"
              aria-label="Aplicar los precios nuevos del catálogo a todos"
            />
            <span>
              <span className="text-xs font-semibold text-foreground-900 block">
                Aplicar los precios nuevos del catálogo (a todos)
              </span>
              <span className="text-[11px] text-foreground-600 block mt-0.5">
                {allApplied ? (
                  <>
                    Sí: se actualizará el precio de{" "}
                    <strong className="text-foreground-800">{formatNumber(priceItems.length)}</strong>{" "}
                    producto{priceItems.length === 1 ? "" : "s"} al valor del PDF (y su costo base al
                    75%). Es el comportamiento de siempre.
                  </>
                ) : appliedPriceCount === 0 ? (
                  <>
                    No: se <strong className="text-foreground-800">conserva el precio actual</strong>{" "}
                    de {formatNumber(priceItems.length)} producto
                    {priceItems.length === 1 ? "" : "s"} (y su costo base). Solo se actualizarán los
                    demás datos (PV, nombre, categoría o foto). Los productos NUEVOS sí entran con el
                    precio del PDF.
                  </>
                ) : (
                  <>
                    Mixto: se aplicará el precio nuevo a{" "}
                    <strong className="text-foreground-800">{formatNumber(appliedPriceCount)}</strong> y
                    se conservará en{" "}
                    <strong className="text-foreground-800">
                      {formatNumber(priceItems.length - appliedPriceCount)}
                    </strong>
                    . Ajusta cada producto con su casilla en la lista.
                  </>
                )}
              </span>
            </span>
          </label>
        )}

        {/* Aviso destacado de productos NUEVOS que se agregarán */}
        {nuevos.length > 0 ? (
          <div className="rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2.5 flex items-start gap-2">
            <i className="ri-alert-line text-accent-300 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground-900">
                Se agregarán {formatNumber(nuevos.length)} producto
                {nuevos.length === 1 ? "" : "s"} NUEVO{nuevos.length === 1 ? "" : "S"} al catálogo
              </p>
              <p className="text-[11px] text-foreground-600 mt-0.5">
                Estos SKU no existían en tu catálogo. Revisa la lista de abajo con cuidado antes de
                confirmar.
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-background-300/40 bg-background-200/40 px-3 py-2.5 flex items-start gap-2">
            <i className="ri-checkbox-circle-line text-foreground-500 mt-0.5" />
            <p className="text-[11px] text-foreground-600">
              No se agregará ningún producto nuevo: todos los SKU del PDF ya existen en tu catálogo.
            </p>
          </div>
        )}

        {/* Resumen */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-md border border-accent-500/30 bg-accent-500/10 px-3 py-2">
            <p className="text-[10px] text-foreground-500 flex items-center gap-1">
              <i className="ri-add-circle-line" /> Nuevos
            </p>
            <p className="text-lg font-bold text-foreground-950">{formatNumber(nuevos.length)}</p>
          </div>
          <div className="rounded-md border border-secondary-500/30 bg-secondary-500/10 px-3 py-2">
            <p className="text-[10px] text-foreground-500 flex items-center gap-1">
              <i className="ri-refresh-line" /> A actualizar
            </p>
            <p className="text-lg font-bold text-foreground-950">
              {formatNumber(updatingItems.length)}
            </p>
          </div>
          <div className="rounded-md border border-background-300/40 bg-background-200/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500 flex items-center gap-1">
              <i className="ri-checkbox-circle-line" /> Sin cambios
            </p>
            <p className="text-lg font-bold text-foreground-950">
              {formatNumber(omitidos.length + keptOnly)}
            </p>
          </div>
        </div>

        {/* A actualizar */}
        {actualizados.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
              <i className="ri-refresh-line text-secondary-100" /> Se actualizarán (
              {formatNumber(updatingItems.length)})
            </h4>
            <div className="rounded-md border border-background-300/40 overflow-hidden">
              <div className="max-h-72 overflow-auto">
                <table className="w-full min-w-[620px] text-left text-xs">
                  <thead className="sticky top-0 bg-background-100/95 backdrop-blur">
                    <tr className="border-b border-background-300/40 text-[10px] uppercase tracking-wide text-foreground-500">
                      <th className="px-3 py-2 font-semibold w-16 text-center">Precio nuevo</th>
                      <th className="px-3 py-2 font-semibold">SKU</th>
                      <th className="px-3 py-2 font-semibold">Producto</th>
                      <th className="px-3 py-2 font-semibold">Cambios</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actualizados.map((item) => {
                      const { row } = item;
                      const withPrice = hasPrice(item);
                      const applied = isApplied(item);
                      const changes = realChanges(item);
                      return (
                        <tr
                          key={row.id}
                          className="border-b border-background-300/20 last:border-0 align-top"
                        >
                          <td className="px-3 py-2 text-center">
                            {withPrice ? (
                              <input
                                type="checkbox"
                                checked={applied}
                                onChange={(e) =>
                                  setKeepIds((prev) =>
                                    e.target.checked
                                      ? prev.filter((id) => id !== row.id)
                                      : [...prev, row.id]
                                  )
                                }
                                className="w-4 h-4 accent-accent-500 cursor-pointer"
                                aria-label={`Aplicar el precio nuevo a ${row.name}`}
                                title={applied ? "Se aplica el precio nuevo" : "Se mantiene el precio actual"}
                              />
                            ) : (
                              <span className="text-foreground-500">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-accent-200">{row.sku}</td>
                          <td className="px-3 py-2 text-foreground-800 min-w-[160px]">{row.name}</td>
                          <td className="px-3 py-2">
                            {changes.length === 0 ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary-500/20 text-secondary-100">
                                el precio anterior se mantiene
                              </span>
                            ) : (
                              <ul className="space-y-1">
                                {changes.map((ch, i) => (
                                  <li key={i} className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-background-300/40 text-foreground-700">
                                      {ch.label}
                                    </span>
                                    <span className="text-foreground-500 line-through">{ch.from}</span>
                                    <i className="ri-arrow-right-line text-foreground-500" />
                                    <span className="text-foreground-950 font-semibold">{ch.to}</span>
                                  </li>
                                ))}
                                {withPrice && !applied && (
                                  <li>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary-500/20 text-secondary-100">
                                      el precio anterior se mantiene
                                    </span>
                                  </li>
                                )}
                              </ul>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Nuevos */}
        {nuevos.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
              <i className="ri-add-circle-line text-accent-200" /> Se crearán (
              {formatNumber(nuevos.length)})
            </h4>
            <ul className="rounded-md border border-background-300/40 divide-y divide-background-300/20 max-h-64 overflow-auto">
              {nuevos.map((row) => (
                <li key={row.id} className="px-3 py-2 text-xs flex items-center gap-2">
                  <span className="font-mono text-accent-200">{row.sku}</span>
                  <span className="text-foreground-800 truncate flex-1">{row.name}</span>
                  <span className="text-foreground-600 whitespace-nowrap">
                    {row.pricePublic ? formatCOP(row.pricePublic) : "sin precio"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Sin cambios */}
        {omitidos.length + keptOnly > 0 && (
          <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-information-line" />
            {formatNumber(omitidos.length + keptOnly)} producto
            {omitidos.length + keptOnly === 1 ? "" : "s"} sin cambios
            {keptOnly > 0 ? " (incluye los que solo cambiaban de precio y lo mantienen)" : ""} — no
            se tocarán.
          </p>
        )}

        {rows.length === 0 && (
          <p className="text-xs text-foreground-500">No hay filas para importar.</p>
        )}
      </div>
    </Modal>
  );
};

export default CatalogImportPreviewModal;